import { createRouter, createWebHistory } from 'vue-router';
import routes from '~pages';
import SignInVue from '@/pages/signin.vue';
import SettingsVue from '@/pages/settings.vue';
import MyAccountVue from '@/pages/profile/index.vue';
import PageNotFound from '@/pages/error/not-found.vue';
import PageUnauthorized from '@/pages/error/unauthorized.vue';
import { isEmpty } from 'lodash';

// Whitelist pages that does not require authentication
const whitelistPages = [
	ROUTE.INDEX.NAME,
	ROUTE.SIGN_IN.NAME,
	ROUTE.SIGN_UP.NAME,
	ROUTE.USER.NAME,
	ROUTE.EVENT_VIEW.NAME,
	ROUTE.HELP.NAME,
	ROUTE.PRICING.NAME,
	ROUTE.PRICING_CALCULATOR.NAME,
	ROUTE.ERROR_404.NAME,
	ROUTE.ERROR_401.NAME,
	ROUTE.TERMS.NAME,
	ROUTE.PRIVACY_POLICY.NAME,
	ROUTE.CANCELLATION_POLICY.NAME,
	ROUTE.COOKIE_POLICY.NAME,
	ROUTE.COMMUNITY_GUIDELINES.NAME,
	ROUTE.ABOUT_US.NAME,
	ROUTE.SEARCH.NAME,
	ROUTE.GROUP.NAME,
	ROUTE.INVITATION.NAME,
	ROUTE.INVITATION_INVALID.NAME,
	ROUTE.INVITATION_VALID.NAME,
	ROUTE.UNSUBSCRIBE.NAME,
	ROUTE.VISIT_JAPAN.NAME,
	ROUTE.VISIT_JAPAN_SEARCH_RESULTS.NAME,
	ROUTE.FEATURE_INVITATION.NAME,
	ROUTE.FEATURE_HOSTING_EVENTS.NAME,
];

const protectedByRecaptchaPages = [
	ROUTE.EVENT_CREATE.NAME,
];

// Set page that has sticky action bar
const pagesWithActionBar = [
	ROUTE.EVENT_EDIT.NAME,
	ROUTE.EVENT_PREVIEW.NAME,
];

// Set page that need to show pictorial marks logo
const pictorialMarksLogoPages = [
	ROUTE.EVENT_VIEW.NAME,
	ROUTE.EVENT_PREVIEW.NAME,
];

const router = createRouter({
	history: createWebHistory(import.meta.env.VITE_SUBDOMAIN),
	routes: [
		{
			path: ROUTE.INDEX.PATH,
			redirect: { name: ROUTE.VISIT_JAPAN.NAME },
		},
		...routes,
		{
			name: ROUTE.SIGN_UP.NAME,
			path: ROUTE.SIGN_UP.PATH,
			component: SignInVue,
			props: { type: AUTH_FORM.SIGN_UP },
		},
		{
			name: ROUTE.PROFILE_ATTENDING.NAME,
			path: ROUTE.PROFILE_ATTENDING.PATH,
			component: MyAccountVue,
			props: { tab: MY_ACCOUNT_TABS.ATTENDING },
		},
		{
			name: ROUTE.PROFILE_HOSTING.NAME,
			path: ROUTE.PROFILE_HOSTING.PATH,
			component: MyAccountVue,
			props: { tab: MY_ACCOUNT_TABS.HOSTING },
		},
		{
			name: ROUTE.SETTINGS_PAYOUTS.NAME,
			path: ROUTE.SETTINGS_PAYOUTS.PATH,
			component: SettingsVue,
			props: { initialTab: SETTING_TABS.PAYOUTS },
		},
		{
			name: ROUTE.SETTINGS_SAVED_CARD.NAME,
			path: ROUTE.SETTINGS_SAVED_CARD.PATH,
			component: SettingsVue,
			props: { initialTab: SETTING_TABS.SAVED_CARDS },
		},
		{
			name: ROUTE.ERROR_404.NAME,
			path: ROUTE.ERROR_404.PATH,
			component: PageNotFound,
		},
		{
			name: ROUTE.ERROR_401.NAME,
			path: ROUTE.ERROR_401.PATH,
			component: PageUnauthorized,
		},
	],
	scrollBehavior(to, _from, savedPosition) {
		if (savedPosition) {
			return savedPosition;
		}
		if (to.hash) {
			return { el: to.hash, top: 80 }; // 80px above the element. `80` is height of header
		}
		return { top: 0 };
	},
});

// Helper function for legacy redirect
function handleLegacyRedirect(to: import('vue-router').RouteLocationNormalized) {
	switch (to.name) {
		case ROUTE.SIGN_IN.NAME:
		case ROUTE.EVENT_CREATE.NAME:
			openLink(`${import.meta.env.VITE_LEGACY_PEATIX_URL}${ROUTE.SIGN_IN.PATH}`, '_self');
			break;
		case ROUTE.SIGN_UP.NAME:
			openLink(`${import.meta.env.VITE_LEGACY_PEATIX_URL}${ROUTE.SIGN_UP.PATH}`, '_self');
			break;
		default:
			openLink(import.meta.env.VITE_LEGACY_PEATIX_URL, '_self');
			break;
	}
}

router.beforeEach(async (to, _from, next) => {
	const authStore = useAuthStore();
	const profileStore = useProfileStore();
	const layoutStore = useLayoutStore();

	// Track root path visit before redirect
	if (to.path === ROUTE.INDEX.PATH) {
		const { trackEvent } = useGtm();
		trackEvent({
			user_id: profileStore.profileId,
			event: GTM_EVENT_NAME.VIEW_LANDING_PAGE_SUCCESS,
		});
	}

	const isLoggedIn = authStore.isLoggedIn;

	// Check if the current page is a Japan Discovery page
	const isJapanDiscoveryPage =
		to.name === ROUTE.VISIT_JAPAN.NAME ||
		to.name === ROUTE.VISIT_JAPAN_SEARCH_RESULTS.NAME;

	let profileCountry = profileStore.profile?.countryId;
	if (isLoggedIn && !profileCountry) {
		try {
			await profileStore.myAccountInfo();
			profileCountry = profileStore.profile?.countryId;
		} catch {
			// Handle error (e.g., force logout, show error, etc.)

			// If not US user, not on exception page: clear user and redirect immediately
			authStore.removeUser();
			handleLegacyRedirect(to);
			return;
		}
	}

	if (isJapanDiscoveryPage) {
		// Always allow Japan Discovery pages
		layoutStore.isAccessible = true;
	} else if (isLoggedIn) {
		// If logged in, check profile country
		if (profileCountry && profileCountry !== DEFAULT_COUNTRY) {
			// Not a US user, not on exception page: clear user and redirect immediately
			authStore.removeUser();
			handleLegacyRedirect(to);
			return;
		}
		// US user, allow access
		layoutStore.isAccessible = true;
	} else {
		// If logged out, allow access
		// TODO: Check if ip address is allowed or not later
		layoutStore.isAccessible = true;
	}

	layoutStore.isPublic = false; // Reset flag to default
	layoutStore.isFullScreen = false; // Reset flag to default

	// Set page's log if it should show pictorial marks logo or combination marks logo
	if (pictorialMarksLogoPages.includes(to.name as string)) {
		layoutStore.isPublic = true;
	}

	// Hide reCAPTCHA badges if page is not use reCAPTCHA
	if (protectedByRecaptchaPages.includes(to.name as string)) {
		window.document.body.classList.add('protect-by-recaptcha');
	} else {
		window.document.body.classList.remove('protect-by-recaptcha');
	}

	const isWhiteListPath = whitelistPages.includes(to.name as string);
	if (!authStore.isLoggedIn && !isWhiteListPath) {
		// If sign out from staging or prod, it should redirect to `{{domain}}/__cc.cgi`
		if (to.path === ROUTE.SIGN_OUT.PATH && import.meta.env.VITE_ENV !== ENVIRONMENT.DEVELOPMENT) {
			const redirectSignoutUrl = `${import.meta.env.VITE_LEGACY_PEATIX_URL}/__cc.cgi`;
			openLinkInCurrentTab(redirectSignoutUrl);
		} else {
			next({
				name: ROUTE.SIGN_IN.NAME,
				...(to.name === ROUTE.EVENT_CREATE.NAME && {
					query: {
						redirect: ROUTE.EVENT_CREATE.NAME,
					},
				}),
			});
		}
	} else if ((authStore.isLoggedIn && (to.name === ROUTE.SIGN_IN.NAME || to.name === ROUTE.SIGN_UP.NAME))) {
		// Prevent user to access sign in & sign up page from URL if they already sign in.
		next({
			path: '',
			replace: true,
		});
	} else {
		next();
	}

	if (to.name && pagesWithActionBar.includes(to.name.toString())) {
		layoutStore.hasActionBar = true;
	} else {
		layoutStore.hasActionBar = false;
	}
});

router.afterEach((to) => {
	// Ignore this route when has query
	if (to.name === ROUTE.SETTINGS.NAME && !isEmpty(to.query)) {
		return;
	}

	// Reset translation state when navigating away from pages that use translation
	const translationPages = [
		ROUTE.VISIT_JAPAN_SEARCH_RESULTS.NAME,
		ROUTE.EVENT_VIEW.NAME,
	];

	// If navigating to a page that doesnt use translation, reset the translation state
	if (!translationPages.includes(to.name as string)) {
		const { setTranslatedView } = useTranslation();
		setTranslatedView(false);
	}

	const { setDocumentTitle } = useSetDocumentTitle();
	setDocumentTitle(to.name);
});

export default router;
